import { ENetwork } from '@NGUtils/enums';
import { getEnumFromValue } from '@NGUtils/functions';
import { axiosClient } from '@services/axios';
import { wornItemsPayload } from '@services/inventory/inventory.services';
import { ESocialLoginPlatforms } from '@settingsSocialRedirection/components/SocialRedirectionPage/SettingsSocialRedirectionPage.enums';
import { EGithubAppState } from '@store/userStore/userStore.enum';
import { formatUserStatistics } from '@store/userStore/userStore.functions';
import { ISignatureWithMessage } from '@store/web3Store/web3Store.type';
import qs from 'qs';
import { IUserInfoParams, IUserStatistics } from 'src/interfaces/user';
import {
	IGetPlatformsResponse,
	IGetUserPlatformsDataResponse,
	IGetUserWalletsDataResponse,
	IPlatformsResponse,
	IUserPlatformsDataResponse,
	IUserPublicProfilResponse,
	IUserQuestContextualModalDataResponse,
	IUserStatisticsResponse,
	IUserWalletsDataResponse
} from './users.services.type';

const putUserInfo = async (info: IUserInfoParams) => {
	const res = await axiosClient.put('/users/update', info);

	return {
		status: res.status,
		info: info
	};
};

const getUserStatistics = async () => {
	const res = (await axiosClient.get('/users/statistics')) as IUserStatisticsResponse;

	return res.data.data;
};

const getAndFormatUserStatistics = async (): Promise<IUserStatistics> => {
	const fetchedUserStatistics = await getUserStatistics();

	return formatUserStatistics(fetchedUserStatistics);
};

const putPlatformUsername = async (platform: ESocialLoginPlatforms, code: string) => {
	return await axiosClient.put(`/users/${platform}-username`, {
		code
	});
};

const deletePlatformUsername = async (platform: ESocialLoginPlatforms) => {
	const res = await axiosClient.delete(`/users/${platform}-username`);
	return {
		...res,
		platform
	};
};

const getUserPlatformsData = async (id?: number): Promise<IGetUserPlatformsDataResponse> => {
	if (!id) {
		throw new Error('id is required');
	}

	const query = qs.stringify(
		{
			filters: {
				id: {
					$eq: id
				}
			},
			fields: ['id', 'githubAppInstalled'],
			populate: ['socials']
		},
		{
			encodeValuesOnly: true
		}
	);

	const { data } = (await axiosClient.get(`users?${query}`)) as IUserPlatformsDataResponse;

	return {
		github: data?.[0]?.socials?.github,
		discord: data?.[0]?.socials?.discord,
		githubAppState: getEnumFromValue(data?.[0]?.githubAppInstalled, EGithubAppState)
	};
};

const getUserWalletsData = async (id?: number): Promise<IGetUserWalletsDataResponse> => {
	if (!id) {
		throw new Error('id is required');
	}

	const query = qs.stringify(
		{
			filters: {
				id: {
					$eq: id
				}
			},
			fields: ['id'],
			populate: ['wallets']
		},
		{
			encodeValuesOnly: true
		}
	);

	const { data } = (await axiosClient.get(`users?${query}`)) as IUserWalletsDataResponse;

	return {
		wallets: {
			[ENetwork.sepolia]: data?.[0]?.wallets?.[ENetwork.sepolia] || null,
			[ENetwork.starknetSepolia]: data?.[0]?.wallets?.[ENetwork.starknetSepolia] || null
		}
	};
};

const getPlatformsUsername = async (id?: number): Promise<IGetPlatformsResponse> => {
	if (!id) {
		throw new Error('id is required');
	}

	const query = qs.stringify(
		{
			filters: {
				id: {
					$eq: id
				}
			},
			fields: ['id'],
			populate: ['socials']
		},
		{
			encodeValuesOnly: true
		}
	);

	const res = (await axiosClient.get(`users?${query}`)) as IPlatformsResponse;

	return {
		github: res.data?.[0]?.socials?.github || null,
		discord: res.data?.[0]?.socials?.discord || null
	};
};

const getUserQuestContextualModalData = async (id: string) => {
	if (!id) {
		return;
	}
	const query = qs.stringify(
		{
			filters: {
				id: {
					$eq: id
				}
			},
			fields: ['id', 'githubAppInstalled']
		},
		{
			encodeValuesOnly: true
		}
	);

	const res = (await axiosClient.get(`users?${query}`)) as IUserQuestContextualModalDataResponse;
	return {
		usersPermissionsUsers: res.data
	};
};

const getUserPublicProfileData = async (hash: string) => {
	const wornItemsQuery = qs.stringify(wornItemsPayload, {
		encodeValuesOnly: true
	});

	const res = (await axiosClient.get(`/users/overview/${hash}?${wornItemsQuery}`)) as IUserPublicProfilResponse;
	return res.data.data;
};

async function putLinkWalletToUser(signatureWithMessage: ISignatureWithMessage) {
	const res = await axiosClient.put(`/users/link-wallet`, signatureWithMessage);

	return {
		...res,
		message: {
			...signatureWithMessage.message
		}
	};
}

export {
	deletePlatformUsername,
	getAndFormatUserStatistics,
	getPlatformsUsername,
	getUserPlatformsData,
	getUserPublicProfileData,
	getUserQuestContextualModalData,
	getUserStatistics,
	getUserWalletsData,
	putLinkWalletToUser,
	putPlatformUsername,
	putUserInfo
};
